// Generat automat din README.md
import React, { useState } from 'react';
import { Brain, TrendingUp, Users, Target, BarChart3, PieChart, Calendar, Filter, Download, RefreshCw, AlertCircle, CheckCircle, Clock, Zap } from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';

const AnalizaPredictiva = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('30d');
  const [selectedModel, setSelectedModel] = useState('behavior');
  const [showInsightsModal, setShowInsightsModal] = useState(false);
  const [isTrainingModel, setIsTrainingModel] = useState(false);
  const [selectedPrediction, setSelectedPrediction] = useState(null);

  // Date pentru analize predictive
  const predictiveData = {
    churnPrediction: [
      { 
        customerId: 'CUST-001', 
        customerName: 'ABC Company SRL', 
        churnProbability: 85, 
        riskLevel: 'Foarte Mare', 
        lastActivity: '2024-02-15',
        predictedChurnDate: '2024-04-20',
        recommendedActions: ['Contact urgent', 'Ofertă personalizată', 'Meeting face-to-face']
      },
      { 
        customerId: 'CUST-002', 
        customerName: 'XYZ Business SA', 
        churnProbability: 65, 
        riskLevel: 'Mare', 
        lastActivity: '2024-03-01',
        predictedChurnDate: '2024-05-15',
        recommendedActions: ['Email de follow-up', 'Survey satisfacție']
      },
      { 
        customerId: 'CUST-003', 
        customerName: 'Tech Solutions Ltd', 
        churnProbability: 35, 
        riskLevel: 'Mediu', 
        lastActivity: '2024-03-10',
        predictedChurnDate: '2024-07-30',
        recommendedActions: ['Check-in lunar', 'Newsletter personalizat']
      },
      { 
        customerId: 'CUST-004', 
        customerName: 'Innovation Hub', 
        churnProbability: 15, 
        riskLevel: 'Scăzut', 
        lastActivity: '2024-03-12',
        predictedChurnDate: '2024-12-01',
        recommendedActions: ['Mențin relația actuală']
      }
    ],
    
    buyingPatterns: [
      { segment: 'Enterprise', avgOrderValue: 15000, frequency: 'Trimestrial', predictedGrowth: 25, confidence: 92 },
      { segment: 'SMB', avgOrderValue: 3500, frequency: 'Lunar', predictedGrowth: 18, confidence: 87 },
      { segment: 'Startup', avgOrderValue: 1200, frequency: 'Semestrial', predictedGrowth: 45, confidence: 78 },
      { segment: 'Government', avgOrderValue: 25000, frequency: 'Anual', predictedGrowth: 12, confidence: 95 }
    ],

    seasonalTrends: [
      { month: 'Ian', actualSales: 45000, predictedSales: 47000, variance: 4.4 },
      { month: 'Feb', actualSales: 52000, predictedSales: 51000, variance: -1.9 },
      { month: 'Mar', actualSales: 48000, predictedSales: 49500, variance: 3.1 },
      { month: 'Apr', actualSales: null, predictedSales: 53000, variance: null },
      { month: 'Mai', actualSales: null, predictedSales: 58000, variance: null },
      { month: 'Iun', actualSales: null, predictedSales: 61000, variance: null }
    ],

    modelPerformance: {
      churnModel: { accuracy: 89.5, precision: 87.2, recall: 91.8, f1Score: 89.4, lastTrained: '2024-03-10' },
      salesModel: { accuracy: 92.1, precision: 90.5, recall: 89.7, f1Score: 90.1, lastTrained: '2024-03-08' },
      behaviorModel: { accuracy: 85.7, precision: 83.9, recall: 88.2, f1Score: 86.0, lastTrained: '2024-03-12' }
    }
  };

  // Statistici generale
  const overallStats = {
    totalPredictions: 2847,
    accuracyRate: 89.2,
    highRiskCustomers: predictiveData.churnPrediction.filter(c => c.riskLevel === 'Foarte Mare' || c.riskLevel === 'Mare').length,
    modelsTrained: 3,
    dataPoints: '2.3M',
    predictiveValue: '€450K'
  };

  const timeframes = [
    { value: '7d', label: '7 zile' },
    { value: '30d', label: '30 zile' },
    { value: '90d', label: '3 luni' },
    { value: '1y', label: '1 an' }
  ];

  const models = [
    { value: 'behavior', label: 'Comportament Clienți' },
    { value: 'churn', label: 'Risc Churn' },
    { value: 'sales', label: 'Predicții Vânzări' },
    { value: 'seasonal', label: 'Tendințe Sezoniere' }
  ];

  const getRiskColor = (riskLevel) => {
    const colors = {
      'Foarte Mare': 'error',
      'Mare': 'warning',
      'Mediu': 'info',
      'Scăzut': 'success'
    };
    return colors[riskLevel] || 'default';
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 90) return 'success';
    if (confidence >= 80) return 'warning';
    return 'error';
  };

  const handleTrainModel = () => {
    setIsTrainingModel(true);
    setTimeout(() => {
      setIsTrainingModel(false);
      // Aici ar fi logica pentru retraining
    }, 3000);
  };

  const handlePredictionClick = (prediction) => {
    setSelectedPrediction(prediction);
    setShowInsightsModal(true);
  };

  const churnColumns = [
    { header: 'Client', key: 'customerName' },
    { 
      header: 'Risc Churn', 
      key: 'churnProbability',
      render: (value) => (
        <div className="flex items-center space-x-2">
          <div className="w-16 bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${value >= 70 ? 'bg-red-500' : value >= 40 ? 'bg-yellow-500' : 'bg-green-500'}`}
              style={{ width: `${value}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium">{value}%</span>
        </div>
      )
    },
    { 
      header: 'Nivel Risc', 
      key: 'riskLevel',
      render: (value) => <Badge variant={getRiskColor(value)}>{value}</Badge>
    },
    { header: 'Ultima Activitate', key: 'lastActivity' },
    { header: 'Data Predicție', key: 'predictedChurnDate' }
  ];

  const patternsColumns = [
    { header: 'Segment', key: 'segment' },
    { 
      header: 'Valoare Medie', 
      key: 'avgOrderValue',
      render: (value) => `€${value.toLocaleString()}`
    },
    { header: 'Frecvență', key: 'frequency' },
    { 
      header: 'Creștere Predicție', 
      key: 'predictedGrowth',
      render: (value) => <span className="text-green-600 font-medium">+{value}%</span>
    },
    { 
      header: 'Încredere', 
      key: 'confidence',
      render: (value) => <Badge variant={getConfidenceColor(value)}>{value}%</Badge>
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Analiză Predictivă AI</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Machine Learning pentru predicția comportamentului și nevoilor clienților
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={handleTrainModel}
            disabled={isTrainingModel}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isTrainingModel ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Antrenare...</span>
              </>
            ) : (
              <>
                <Brain className="w-4 h-4" />
                <span>Antrenează Modelul</span>
              </>
            )}
          </button>

          <button className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
            <Download className="w-4 h-4" />
            <span>Export Predicții</span>
          </button>
        </div>
      </div>

      {/* Statistici generale */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <Card>
          <div className="flex items-center space-x-3">
            <Brain className="w-8 h-8 text-purple-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{overallStats.totalPredictions.toLocaleString()}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Predicții Generate</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <Target className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{overallStats.accuracyRate}%</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Acuratețe Modele</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-8 h-8 text-red-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{overallStats.highRiskCustomers}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Clienți Risc Mare</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <Zap className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{overallStats.modelsTrained}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Modele Active</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <BarChart3 className="w-8 h-8 text-orange-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{overallStats.dataPoints}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Puncte de Date</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center space-x-3">
            <TrendingUp className="w-8 h-8 text-yellow-600" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{overallStats.predictiveValue}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Valoare Predicții</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Controale filtrare */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Perioada</label>
            <select
              value={selectedTimeframe}
              onChange={(e) => setSelectedTimeframe(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            >
              {timeframes.map(timeframe => (
                <option key={timeframe.value} value={timeframe.value}>
                  {timeframe.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Model Analiza</label>
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            >
              {models.map(model => (
                <option key={model.value} value={model.value}>
                  {model.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Performanța modelelor */}
      <Card title="Performanța Modelelor ML" icon={Brain}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Object.entries(predictiveData.modelPerformance).map(([modelKey, metrics]) => (
            <div key={modelKey} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3 capitalize">
                {modelKey.replace('Model', ' Model')}
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Acuratețe:</span>
                  <span className="font-medium text-green-600">{metrics.accuracy}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Precizie:</span>
                  <span className="font-medium text-blue-600">{metrics.precision}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Recall:</span>
                  <span className="font-medium text-yellow-600">{metrics.recall}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">F1-Score:</span>
                  <span className="font-medium text-purple-600">{metrics.f1Score}%</span>
                </div>
                <div className="pt-2 border-t dark:border-gray-700">
                  <span className="text-xs text-gray-500">
                    Ultima antrenare: {new Date(metrics.lastTrained).toLocaleDateString('ro-RO')}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Predicții Churn */}
        <Card title="Predicții Churn - Clienți în Risc" icon={AlertCircle}>
          <Table
            columns={churnColumns}
            data={predictiveData.churnPrediction}
            onRowClick={(row) => handlePredictionClick(row)}
          />
        </Card>

        {/* Pattern-uri de cumpărare */}
        <Card title="Pattern-uri de Cumpărare" icon={TrendingUp}>
          <Table
            columns={patternsColumns}
            data={predictiveData.buyingPatterns}
          />
        </Card>
      </div>

      {/* Tendințe sezoniere */}
      <Card title="Predicții Sezoniere - Vânzări" icon={Calendar}>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          {predictiveData.seasonalTrends.map((trend) => (
            <div key={trend.month} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <h3 className="font-semibold text-gray-900 dark:text-white text-center mb-2">{trend.month}</h3>
              <div className="space-y-2">
                {trend.actualSales && (
                  <div>
                    <p className="text-xs text-gray-500">Vânzări reale:</p>
                    <p className="font-medium text-gray-900 dark:text-white">€{trend.actualSales.toLocaleString()}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-gray-500">Predicție:</p>
                  <p className="font-medium text-blue-600">€{trend.predictedSales.toLocaleString()}</p>
                </div>
                {trend.variance !== null && (
                  <div>
                    <p className="text-xs text-gray-500">Variație:</p>
                    <p className={`font-medium ${trend.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {trend.variance >= 0 ? '+' : ''}{trend.variance}%
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Modal cu insights detaliate */}
      {showInsightsModal && selectedPrediction && (
        <Modal
          isOpen={showInsightsModal}
          onClose={() => setShowInsightsModal(false)}
          title={`Analiza Detaliata - ${selectedPrediction.customerName}`}
        >
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{selectedPrediction.customerName}</h2>
                <p className="text-gray-600 dark:text-gray-400">ID: {selectedPrediction.customerId}</p>
              </div>
              <Badge variant={getRiskColor(selectedPrediction.riskLevel)} className="text-lg p-2">
                {selectedPrediction.riskLevel}
              </Badge>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <h3 className="font-semibold text-red-800 dark:text-red-400">Risc Churn</h3>
                </div>
                <p className="text-3xl font-bold text-red-600">{selectedPrediction.churnProbability}%</p>
                <p className="text-sm text-red-700 dark:text-red-400">probabilitate</p>
              </div>

              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Clock className="w-5 h-5 text-yellow-600" />
                  <h3 className="font-semibold text-yellow-800 dark:text-yellow-400">Data Predicție</h3>
                </div>
                <p className="text-lg font-bold text-yellow-600">{new Date(selectedPrediction.predictedChurnDate).toLocaleDateString('ro-RO')}</p>
                <p className="text-sm text-yellow-700 dark:text-yellow-400">churn estimat</p>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Acțiuni Recomandate</h3>
              <div className="space-y-2">
                {selectedPrediction.recommendedActions.map((action, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                    <span className="text-blue-800 dark:text-blue-400">{action}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t dark:border-gray-700">
              <button
                onClick={() => setShowInsightsModal(false)}
                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Închide
              </button>
              <button className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors">
                Inițiază Acțiuni
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AnalizaPredictiva;